import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { firstDayOfMonth, lastDayOfMonth } from '@progress/kendo-date-math';
import { StorageInvoice } from './storage-invoice.model';
import { StorageInvoiceSummary } from './storage-invoice-summary.model';
import { RailcarStorageHistory, toDateOnlyString } from '../car-repair-billing/railcar-storage-history.model';
import { SendToIntacctInvoice } from '../../../app/components/common/send-to-intacct/send-to-intacct-invoice.model';
import { map } from 'rxjs/operators';
import { StorageArInvoiceSummary } from './storage-ar-invoice-summary.model';
import { StorageInvoiceDetail } from './storage-invoice-detail.model';
import { SwitchInvoice } from './switch-invoice.model';
import { Facility } from '../facility/facility.model';

@Injectable({providedIn: 'root'})
export class StorageService {

	private url: string;

	constructor(private httpClient: HttpClient) {
		this.url = 'api/Storage';
	}

	public searchStorageInvoices(sentToIntacct: boolean, dateForClosedInvoices: Date): Observable<StorageInvoiceSummary[]> {
		if (sentToIntacct && dateForClosedInvoices) {
			return this.httpClient.get<StorageInvoiceSummary[]>(this.url + '/SearchStorageInvoices' +
			'?closedInvoicesMonth=' + (dateForClosedInvoices.getMonth() + 1) +
			'&closedInvoicesYear=' + dateForClosedInvoices.getFullYear());
		} else {
			return this.httpClient.get<StorageInvoiceSummary[]>(this.url + '/SearchStorageInvoices');
		}
	}

	public getStorageInvoiceDetails(id: number, dateForClosedInvoices: Date): Observable<StorageInvoice> {
		if (dateForClosedInvoices) {
			return this.httpClient.get<StorageInvoice>(this.url + '/GetStorageInvoiceDetails?storageInvoiceId=' + id +
			'&closedInvoicesMonth=' + (dateForClosedInvoices.getMonth() + 1) +
			'&closedInvoicesYear=' + dateForClosedInvoices.getFullYear());
		} else {
			return this.httpClient.get<StorageInvoice>(this.url + '/GetStorageInvoiceDetails?storageInvoiceId=' + id);
		}
	}

	public searchStorageInvoiceByRailcarID(railcarID: number): Observable<StorageInvoiceSummary[]> {
		return this.httpClient.get<StorageInvoiceSummary[]>(this.url + '/SearchStorageInvoicesByRailcarId?railcarID=' + railcarID);
	}

	public getStorageArInvoiceDetails(rebillingId: number): Observable<StorageInvoiceDetail[]> {
		return this.httpClient.get<StorageInvoiceDetail[]>(this.url + '/GetStorageArInvoiceDetails?rebillingId=' + rebillingId);
	}

	public upsertStorageInvoice(storageInvoice: StorageInvoice, invoiceDetailChange: boolean): Observable<StorageInvoice> {
		return this.httpClient.put<StorageInvoice>(this.url + '/upsertStorageInvoice/' + invoiceDetailChange, storageInvoice);
	}

	public addNewStorageInvoiceDetail(storageInvoiceDetail: StorageInvoiceDetail, storageInvoiceId: number): Observable<StorageInvoiceDetail> {
		return this.httpClient.post<StorageInvoiceDetail>(this.url + '/addNewStorageInvoiceDetail?storageInvoiceId=' + storageInvoiceId, storageInvoiceDetail);
	}

	public updateRailcarStorageDateInOut(railcarStorageHistory: RailcarStorageHistory): Observable<RailcarStorageHistory> {
		return this.httpClient.post<RailcarStorageHistory>(this.url + '/UpdateRailcarStorageDateInOut', railcarStorageHistory);
	}
	public updateRailcarsStorageDateInOut(railcarsStorageHistory: RailcarStorageHistory[]): Observable<RailcarStorageHistory[]> {
		return this.httpClient.post<RailcarStorageHistory[]>(this.url + '/UpdateRailcarsStorageDateInOut', railcarsStorageHistory);
	}
	public setInvoiceWorkingStatus(id: number, isWorked: boolean): Observable<any> {
		return this.httpClient.put(this.url + '/setInvoiceWorkingStatus?id=' + id + '&isWorked=' + isWorked, null);
	}

	public setSwitchInvoiceWorkingStatus(id: number, isWorked: boolean): Observable<any> {
		return this.httpClient.put(this.url + '/setSwitchInvoiceWorkingStatus?id=' + id + '&isWorked=' + isWorked, null);
	}

	public generateAPStorageInvoices() {
		return this.httpClient.get(this.url + '/GenerateAPStorageInvoices');
	}

	public getOpenPeriodStart(): Observable<Date> {
		return this.httpClient.get<Date>(this.url + '/GetOpenPeriodStart');
	}

	public getVendorDueDateTerm(shopId: number): Observable<number> {
		return this.httpClient.get<number>(this.url + '/GetVendorDueDateTerm/' + shopId);
	}

	public getPurchaseOrder(invoiceNumber: string, shopId: number): Observable<any> {
		return this.httpClient.get<any>(this.url + '/getPurchaseOrder/?invoiceNumber=' + invoiceNumber + '&shopId=' + shopId);
	}

	public sendAPStorageAccountsPayable(invoiceId: number, openPeriodStart: Date, invoiceDate: Date,
			invoiceDueDate: Date, glPostingDate: Date): Observable<any[]> {
		return this.httpClient.get<any[]>(this.url + '/sendAccountsPayables?carRepairBillingInvoiceId=' + invoiceId +
			'&openPeriodStart=' + openPeriodStart.toLocaleDateString() + '&invoiceDate=' + invoiceDate.toLocaleDateString() +
			'&dueDate=' + invoiceDueDate.toLocaleDateString() + '&glPostingDate=' + glPostingDate.toLocaleDateString());
	}

	public sendAPStorageAccountsPayables(invoices: SendToIntacctInvoice[]): Observable<any> {
		return this.httpClient.post<any>(this.url + '/sendAccountsPayables',
			invoices.map(x => { return {item1: x.id, item2: x.glPostingDate, item3: x.accountingDueDate}}));
	}

	public updateRailcarsStorageFees(railcarStorageHistory: RailcarStorageHistory[], storageInvoiceId: number): Observable<any> {
		const payload = railcarStorageHistory.map(x => ({
			...x,
			feeEffectiveDate: x.feeEffectiveDate ? toDateOnlyString(x.feeEffectiveDate) : undefined
		}));
		return this.httpClient.post<any>(this.url + '/UpdateRailcarsStorageFees?storageInvoiceId=' + storageInvoiceId, payload);
	}

	public reverseAPStorageAccountsPayable(invoiceId: number): Observable<any[]> {
		return this.httpClient.get<any[]>(this.url + '/reverseAPStorageAccountsPayable?storageInvoiceId=' + invoiceId);
	}

	public createArPreview(invoices: any[], billingPeriod: Date): Observable<any[]> {
		return this.httpClient.post<any[]>(this.url + '/createarpreview', invoices,
		{
			params: {
				'startDate': firstDayOfMonth(billingPeriod).toDateString(),
				'endDate': lastDayOfMonth(billingPeriod).toDateString()
			}
		})
		.pipe(map(x => x.map(y => new StorageArInvoiceSummary(y))));
	}

	public searchArInvoices(billingPeriod: Date): Observable<any> {
		return this.httpClient.get<StorageArInvoiceSummary[]>(this.url + '/GetStorageArInvoices?billingperiod=' + billingPeriod.toLocaleDateString());
	}

	public saveSwitchInvoice(switchInvoice: SwitchInvoice, storageInvoiceId: number): Observable<SwitchInvoice> {
		return this.httpClient.post<SwitchInvoice>(this.url + '/saveSwitchInvoice', switchInvoice, {
			params: {
				'storageInvoiceId': storageInvoiceId.toString()
			}
		});
	}

	public getRailcarActiveStorageLocation(carId: number): Observable<Facility> {
		return this.httpClient.get<Facility>(this.url + '/GetRailcarActiveStorageLocation', {
			params: {
				'carId': carId.toString()
			}
		});

	}
	public sendReadyForReview(invoiceIds: number[]): Observable<any> {
		return this.httpClient.post<any>(this.url + '/sendReadyForReview', invoiceIds);
	}

	public sendToApproved(invoiceIds: number[]): Observable<any> {
		return this.httpClient.post<any>(this.url + '/sendToApproved', invoiceIds);
	}
	
	public getActiveRailcarStorageHistoryByRailcar(railcarId: number): Observable<RailcarStorageHistory> {
		return this.httpClient.get<RailcarStorageHistory>(this.url + '/GetActiveRailcarStorageHistoryByRailcar' +
			'?railcarId=' + railcarId);
	}
}
